import { render } from 'preact';
import type { VNode } from 'preact';
import {
  BACKPACK_SLOTS,
  ITEMS,
  ItemLocation,
  MAX_DURABILITY,
  QUICK_SLOTS,
  Rarity,
  RARITY_COLORS,
  SATCHEL_SLOTS,
  SABEL_NAME,
  durabilityOf,
  isBroken,
  isDamageable,
} from '@stillsun/shared';
import type { ItemStack, ItemKind } from '@stillsun/shared';
import './overlay.css';
import { FeedbackModal } from './feedback-form.js';
import { submitFeedback, type FeedbackCollector } from '../game/feedback.js';
import type { FrameCapture } from '../render/capture.js';
import { sound } from '../audio/sound.js';
import { progressStore } from '../net/progress-store.js';
import { Icon, ItemIcon, type IconName, itemKindIcon } from './icons.js';
import { SettingsTab } from './hub/Settings.js';
import { Loadout } from './hub/Loadout.js';
import { Vendor } from './hub/Vendor.js';
import { ItemMenu, ItemTooltip, baseItemEntries, type MenuEntry } from './hub/common.js';
import type { HubHost } from './hub/index.js';

export interface OverlayHost {
  collector: FeedbackCollector;
  capture: FrameCapture;
  /** Read live — the JWT can arrive after the overlay mounts. */
  token: () => string;
  screenshotDefault: () => boolean;
  /** C2S.Interact Take for one stack, or every stack in the open container. */
  onTake: (uid: number) => void;
  /** All stacks in the currently open container/bag — the Take All button, or the host's
   * T hotkey via `OverlayHandle.takeAll()`. */
  onTakeAll: () => void;
  onUse: (uid: number) => void;
  onDrop: (uid: number) => void;
  /** The loot panel was dismissed. */
  onCloseLoot: () => void;
  /** Leave the raid and go back to the hub. */
  onReturnToHub: () => void;
  /** A panel opened or closed; the host suspends movement/fire while one is up. */
  onPanelChange: (open: boolean) => void;
  /** A setting changed in the in-raid Settings panel; same hook the hub uses. */
  onSettingsChanged: () => void;
  /**
   * True when a keypress belongs to the world rather than to the hub, a text field or the chat
   * box. The overlay claims exactly one key of its own (C, the character sheet) and copies
   * `raidbar.ts`'s guards; this is the same predicate that gates the menu strip's own caps.
   */
  isActive: () => boolean;
}

export interface OverlayHandle {
  clearRaid(): void;
  setInventory(items: readonly ItemStack[]): void;
  showLoot(containerId: number, items: readonly ItemStack[]): void;
  closeLoot(): void;
  setInventoryOpen(open: boolean): void;
  toggleInventory(): void;
  /** T pressed while a UI panel is open. No-op unless the loot panel is actually up. */
  takeAll(): void;
  /** '' hides the interact prompt. */
  setPrompt(text: string): void;
  openFeedback(seed: string): void;
  closeFeedback(): void;
  /** The in-raid Settings panel (L, or the bar's Settings button). Same tab as Lanternfall's. */
  toggleSettings(): void;
  /** The character sheet — the same paperdoll the hub draws, read-only out here. C toggles it. */
  toggleCharacter(): void;
  /** Sabel's counter. Opened by talking to her; the same market panel Lanternfall draws. */
  openVendor(): void;
  /** Mark unlocks, level ups, contract completions — see `ToastTone`. */
  pushToast(text: string, tone: ToastTone): void;
  showLost(reason: string): void;
  hideCard(): void;
  /** True when any panel or card is up (the host suspends look/movement). */
  readonly panelOpen: boolean;
  readonly lootContainerId: number;
  /** ESC: closes the topmost thing. Returns false when there was nothing to close. */
  escape(): boolean;
  destroy(): void;
}

/** The death card. Extraction had its own summary screen once (§7.15/16); now the only
 * way this card shows is a death, so it is just the reason the run ended. */
interface CardState {
  reason: string;
}

interface OverlayState {
  raidActive: boolean;
  inventory: ItemStack[];
  lootId: number;
  loot: ItemStack[];
  invOpen: boolean;
  /** The bag tile the player has picked, whose Use/Drop strip is showing. */
  bagSel: number | null;
  prompt: string;
  feedback: { open: boolean; seed: string };
  settingsOpen: boolean;
  /** The character sheet (C). Read-only out here — see `CharacterPanel`. */
  charOpen: boolean;
  /** Sabel's counter, opened by talking to her. Bumping `vendorNonce` re-reads the market. */
  vendorOpen: boolean;
  vendorNonce: number;
  /** The right-click menu, by stack uid so a re-sent inventory cannot strand it on a dead row. */
  menu: { uid: number; x: number; y: number } | null;
  /** The hover card, same addressing, same reason. */
  tip: { uid: number; x: number; y: number } | null;
  card: CardState | null;
  /**
   * Bottom-centre toasts, newest last: loot pickups, Mark unlocks, level ups, contract
   * completions. A queue rather than a single slot because M3 can land two of these in
   * the same tick (a Mark and the level it pushed you over).
   */
  toasts: ToastItem[];
}

/** Tones map to the Mark tiers plus one for progression events. */
export type ToastTone = 'loot' | 'bronze' | 'amber' | 'hymn' | 'level';

interface ToastItem {
  id: number;
  text: string;
  tone: ToastTone;
}

/** Toast lifetimes. Marks earn a longer read than "+ Hull Scrap x3". */
const TOAST_MS: Record<ToastTone, number> = {
  loot: 1500,
  bronze: 3200,
  amber: 3600,
  hymn: 4200,
  level: 3600,
};
/** More than this on screen at once and the bottom of the HUD is a wall of text. */
const MAX_TOASTS = 4;

const rarityCss = (itemId: string): string => {
  const def = ITEMS[itemId];
  const hex = RARITY_COLORS[def?.rarity ?? 0] ?? RARITY_COLORS[0];
  return `#${hex.toString(16).padStart(6, '0')}`;
};

/** Rail/border colour for an item's rarity. Moon (`#f2effa`) is the panel's own bright text
 * colour, so a moon rail would vanish — and on the M6 ladder moon is *Common*, index 0, not
 * the top rung; Legendary's orange reads fine as a rail and keeps its own colour. So the thin
 * wine-2 substitution is Common's now (the name text still reads moon via `rarityCss`, so the
 * tier itself is unmistakable). */
const railCss = (itemId: string): string =>
  ITEMS[itemId]?.rarity === Rarity.Common ? 'var(--wine-2)' : rarityCss(itemId);

const nameOf = (stack: ItemStack): string => ITEMS[stack.itemId]?.name ?? stack.itemId;

/**
 * The mark on a quick-slot row. A quick slot is read at a glance mid-fight, so the one thing that
 * actually lives there — a heal — keeps its own drawing rather than a generic "consumable" vial.
 * (Phase 2c retired the magazine refill along with the guns.) Everything else in the bag draws
 * `ItemIcon`, which is the atlas sprite where the piece has a mesh.
 */
const itemIcon = (stack: ItemStack): IconName => {
  const def = ITEMS[stack.itemId];
  if (def?.effect?.heal !== undefined) return 'stim';
  return itemKindIcon(def?.kind);
};

const isConsumable = (stack: ItemStack): boolean => ITEMS[stack.itemId]?.kind === 'consumable';

// "No idea what items do" — every loot/inventory row now carries kind, description,
// consumable effect and vendor value so a player never has to alt-tab to a wiki.
const KIND_LABEL: Record<ItemKind, string> = {
  weapon: 'Weapon',
  armor: 'Armour',
  consumable: 'Consumable',
  material: 'Material',
  valuable: 'Valuable',
  key: 'Key',
};

const kindLabel = (stack: ItemStack): string => {
  const kind = ITEMS[stack.itemId]?.kind;
  return kind ? KIND_LABEL[kind] : '';
};

/** "heals 40 over 4 s" — null for anything without a consumable effect. */
const effectLabel = (stack: ItemStack): string | null => {
  const fx = ITEMS[stack.itemId]?.effect;
  if (!fx || fx.heal === undefined) return null;
  return `heals ${String(fx.heal)} over ${String((fx.overMs ?? 0) / 1000)} s`;
};

/** Second row of a loot/inventory line: desc, effect (if any), vendor value — one line,
 * truncated with an ellipsis rather than a tooltip (panels are too small for hover). */
const metaLine = (stack: ItemStack): string => {
  const def = ITEMS[stack.itemId];
  const parts = [def?.desc ?? ''];
  const fx = effectLabel(stack);
  if (fx) parts.push(fx);
  parts.push(`${String(def?.value ?? 0)} Wick each`);
  return parts.join(' · ');
};

const ItemMeta = ({ stack }: { stack: ItemStack }): VNode => (
  <div class="ov-meta">{metaLine(stack)}</div>
);

/**
 * The in-raid bag is a grid, not a list (owner, 2026-09-04: "make the backpack 12 slots by
 * default. have it stack 4 columns with 3 rows and live in the bottom right corner"). Four columns
 * is the constant the grid, the row maths and the hub's Stash all hang off; a skill node that
 * grants a thirteenth square simply adds a fourth row.
 */
const BAG_COLS = 4;

/**
 * Lay stacks into fixed cells. Slot indices are the server's, but a duplicate or out-of-range one
 * falls into the first free square rather than vanishing — an item the player cannot see mid-raid
 * is worse than one in the wrong square. (Same rule as the hub's Stash grid.)
 */
function bagCells(items: readonly ItemStack[], loc: number, count: number): (ItemStack | null)[] {
  const cells: (ItemStack | null)[] = new Array<ItemStack | null>(count).fill(null);
  const strays: ItemStack[] = [];
  for (const s of items) {
    if (s.location !== loc) continue;
    if (s.slot >= 0 && s.slot < count && cells[s.slot] === null) cells[s.slot] = s;
    else strays.push(s);
  }
  for (const s of strays) {
    const free = cells.indexOf(null);
    if (free < 0) break;
    cells[free] = s;
  }
  return cells;
}

/**
 * How many backpack squares to draw: twelve, or more once a skill node has granted them. The
 * overlay never receives `PlayerModifiers`, so capacity is inferred from the highest backpack slot
 * the *server* has filled — the server stays the only authority on how big the bag is — rounded up
 * to a whole row so the grid never ends mid-line.
 */
function bagSlotCount(items: readonly ItemStack[]): number {
  let highest = BACKPACK_SLOTS - 1;
  for (const s of items) {
    if (s.location === ItemLocation.Backpack && s.slot > highest) highest = s.slot;
  }
  // Clamped to four rows. A single stack arriving at slot 99 would otherwise draw a hundred
  // squares and take the panel off the screen; `bagCells` already sweeps anything past the last
  // square into the first free one, so nothing is hidden by the clamp.
  return Math.min(Math.ceil((highest + 1) / BAG_COLS) * BAG_COLS, BAG_COLS * 4);
}

export function mountOverlay(root: HTMLElement, host: OverlayHost): OverlayHandle {
  let state: OverlayState = {
    raidActive: false,
    inventory: [],
    lootId: 0,
    loot: [],
    invOpen: false,
    bagSel: null,
    prompt: '',
    feedback: { open: false, seed: '' },
    settingsOpen: false,
    charOpen: false,
    vendorOpen: false,
    vendorNonce: 0,
    menu: null,
    tip: null,
    card: null,
    toasts: [],
  };

  let lastPanelOpen = false;
  let ticker: ReturnType<typeof setInterval> | null = null;
  const toastTimers = new Set<ReturnType<typeof setTimeout>>();
  let toastSeq = 0;

  const panelOpen = (): boolean =>
    state.lootId !== 0 ||
    state.invOpen ||
    state.feedback.open ||
    state.settingsOpen ||
    state.charOpen ||
    state.vendorOpen ||
    state.card !== null;

  /** The stack a uid names, anywhere in the carried list. */
  const stackByUid = (uid: number | null | undefined): ItemStack | null =>
    uid === null || uid === undefined ? null : (state.inventory.find((s) => s.uid === uid) ?? null);

  const set = (patch: Partial<OverlayState>): void => {
    state = { ...state, ...patch };
    draw();
    const open = panelOpen();
    if (open !== lastPanelOpen) {
      lastPanelOpen = open;
      host.onPanelChange(open);
    }
  };

  // --- pieces -------------------------------------------------------------

  const Prompt = (): VNode | null => {
    if (state.prompt.length === 0 || panelOpen()) return null;
    const key = 'E';
    // The middot is the game's own separator — the kill feed, the chat and the quest toasts all
    // use it — and it is what pulls the key away from the verb: "E · SPEAK", not "E SPEAK".
    return (
      <div class="ov-prompt">
        <b>{key}</b> · {state.prompt}
      </div>
    );
  };

  /** Post-take feedback: "+ Hull Scrap x3" for 1.5 s when an S2C.Inventory arrives while
   * the loot panel is open — see setInventory() for the diff that triggers it. */
  const Toasts = (): VNode | null => {
    if (state.toasts.length === 0) return null;
    return (
      <div class="ov-toasts">
        {state.toasts.map((t) => (
          <div key={t.id} class={`ov-toast ${t.tone}`}>
            {/* A Mark gets its glyph in front of the words; a loot line does not need one. */}
            {t.tone === 'bronze' || t.tone === 'amber' || t.tone === 'hymn' ? (
              <Icon name="marks" size={14} class="ov-toast-ic" />
            ) : null}
            {t.text}
          </div>
        ))}
      </div>
    );
  };

  const QuickBar = (): VNode | null => {
    if (!state.raidActive || state.card !== null) return null;
    const slots: (ItemStack | null)[] = [];
    for (let i = 0; i < QUICK_SLOTS; i++) {
      slots.push(
        state.inventory.find((s) => s.location === ItemLocation.Quick && s.slot === i) ?? null,
      );
    }
    return (
      <div class="ov-quick">
        {slots.map((stack, i) => (
          <div
            key={i}
            class={stack ? 'ov-slot filled' : 'ov-slot'}
            style={stack ? { borderColor: railCss(stack.itemId) } : undefined}
            onClick={() => {
              if (!stack) return;
              sound.play('ui_tick');
              host.onUse(stack.uid);
            }}
          >
            <span class="k">{String(i + 1)}</span>
            {stack ? <Icon name={itemIcon(stack)} size={16} class="ov-slotic" /> : null}
            <span class="n">{stack ? nameOf(stack) : '—'}</span>
            {stack && stack.qty > 1 ? <span class="q">x{String(stack.qty)}</span> : null}
          </div>
        ))}
      </div>
    );
  };

  const LootPanel = (): VNode | null => {
    if (state.lootId === 0) return null;
    return (
      <div class="ov-panel left">
        <div class="ov-head">
          {/* The container's row id is bookkeeping, not a name — "Container 12" was the
              heading over every locker in the game. What is in it is the only question. */}
          <h3>Salvage</h3>
          <button
            type="button"
            class="ov-x"
            onClick={() => {
              closeLoot();
            }}
          >
            ✕
          </button>
        </div>
        <div class="ov-list">
          {state.loot.length === 0 ? (
            <div class="ov-empty">Picked clean.</div>
          ) : (
            state.loot.map((stack) => (
              <div
                key={stack.uid}
                class="ov-item"
                style={{ borderLeftColor: railCss(stack.itemId) }}
              >
                <div class="ov-row-top">
                  <span class="name" style={{ color: rarityCss(stack.itemId) }}>
                    {nameOf(stack)}
                  </span>
                  <span class="kind">{kindLabel(stack)}</span>
                  <span class="qty">x{String(stack.qty)}</span>
                  <button
                    type="button"
                    class="ov-act"
                    onClick={() => {
                      host.onTake(stack.uid);
                    }}
                  >
                    Take
                  </button>
                </div>
                <ItemMeta stack={stack} />
              </div>
            ))
          )}
        </div>
        <div class="ov-foot">
          <button
            type="button"
            class="ov-act"
            disabled={state.loot.length === 0}
            onClick={() => {
              sound.play('ui_confirm');
              host.onTakeAll();
            }}
          >
            Take all · T
          </button>
        </div>
      </div>
    );
  };

  /**
   * One bag square. Requiem, and deliberately the same object as a WoW-bar slot (`.rbslot`): a
   * flat plum-2 square, a 2px edge, no radius and no glow, the item's rarity on the left rail, its
   * glyph in the middle and its count bottom-right in Bebas. The name lives in the strip under the
   * grid rather than on a 52px tile, where it would be three words of ellipsis.
   */
  const Tile = (props: { stack: ItemStack | null; wide?: boolean }): VNode => {
    const stack = props.stack;
    const def = stack === null ? undefined : ITEMS[stack.itemId];
    const wears = isDamageable(def);
    const dur = stack === null ? MAX_DURABILITY : durabilityOf(stack);
    const broken = stack !== null && isBroken(stack);
    const pick = (): void => {
      if (!stack) return;
      sound.play('ui_tick');
      set({ bagSel: state.bagSel === stack.uid ? null : stack.uid, menu: null, tip: null });
    };
    return (
      <div
        class={`ov-tile${stack ? ' filled' : ''}${stack && stack.uid === state.bagSel ? ' sel' : ''}${props.wide === true ? ' wide' : ''}${broken ? ' broken' : ''}`}
        style={stack ? { borderLeftColor: railCss(stack.itemId) } : undefined}
        role={stack ? 'button' : 'presentation'}
        tabIndex={stack ? 0 : -1}
        aria-label={
          stack
            ? `${nameOf(stack)}${stack.qty > 1 ? ` ×${String(stack.qty)}` : ''}${broken ? ', broken' : ''}`
            : 'Empty slot'
        }
        onClick={pick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pick();
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!stack) return;
          set({ menu: { uid: stack.uid, x: e.clientX, y: e.clientY }, tip: null });
        }}
        // Anchored where the cursor entered the square, and *not* followed on mousemove: this
        // tree is re-rendered whole on every `set`, and the client's frame is CPU-bound
        // (see the M6 perf pass). A card that holds still over a 52px tile reads fine anyway.
        onMouseEnter={(e) => {
          if (stack && state.menu === null) {
            set({ tip: { uid: stack.uid, x: e.clientX, y: e.clientY } });
          }
        }}
        onMouseLeave={() => {
          if (state.tip !== null) set({ tip: null });
        }}
      >
        {def ? (
          <ItemIcon
            def={def}
            size={28}
            class="ov-tile-ic"
            {...(wears ? { durability: dur } : {})}
          />
        ) : stack ? (
          <Icon name={itemIcon(stack)} size={18} class="ov-tile-ic" />
        ) : null}
        {stack && stack.qty > 1 ? <span class="ov-tile-q">{String(stack.qty)}</span> : null}
        {stack?.insured === true ? <span class="ov-tile-ins" title="Insured" /> : null}
      </div>
    );
  };

  const BagSection = (props: {
    label: string;
    hint?: string;
    cols: number;
    cells: (ItemStack | null)[];
    wide?: boolean;
  }): VNode => (
    <div class="ov-bag-sec">
      <div class="ov-bag-label">
        {props.label}
        {props.hint === undefined ? null : <span class="hint">{props.hint}</span>}
      </div>
      <div class={`ov-bag-grid c${String(props.cols)}`}>
        {props.cells.map((stack, i) => (
          <Tile
            key={stack ? `u${String(stack.uid)}` : `e${String(i)}`}
            stack={stack}
            {...(props.wide === true ? { wide: true } : {})}
          />
        ))}
      </div>
    </div>
  );

  const InventoryPanel = (): VNode | null => {
    if (!state.invOpen) return null;
    const inv = state.inventory;
    const count = bagSlotCount(inv);
    const bag = bagCells(inv, ItemLocation.Backpack, count);
    const filled = bag.filter((c) => c !== null).length;
    const held: (ItemStack | null)[] = [
      inv.find((s) => s.location === ItemLocation.Primary) ?? null,
      inv.find((s) => s.location === ItemLocation.Secondary) ?? null,
    ];
    const sel = state.bagSel === null ? null : (inv.find((s) => s.uid === state.bagSel) ?? null);
    // The loot panel takes the phone-sized screen; the bag yields to it, as it always has.
    const cls = state.lootId !== 0 ? 'ov-bag secondary' : 'ov-bag';
    return (
      <div class={cls}>
        <div class="ov-bag-head">
          <h3>Carried</h3>
          {/* The bag is the panel a player is most often looking at when something goes
              wrong, so the feedback terminal hangs off it as well as off F8. */}
          <button
            type="button"
            class="ov-act ghost"
            onClick={() => {
              openFeedback('');
            }}
          >
            Report
          </button>
          <button
            type="button"
            class="ov-x"
            onClick={() => {
              set({ invOpen: false, bagSel: null });
            }}
          >
            ✕
          </button>
        </div>

        {/* The satchel used to sit under the backpack in one scrolling list. With the bag pinned
            to the bottom-right corner that stack would run into the WoW bar, so the satchel (and
            the two weapons and the quick slots beside it) dock to the *left* of the grid. */}
        <div class="ov-bag-body">
          <div class="ov-bag-side">
            <BagSection label="Held" cols={2} cells={held} />
            <BagSection
              label="Satchel"
              hint="kept on death"
              cols={2}
              cells={bagCells(inv, ItemLocation.Satchel, SATCHEL_SLOTS)}
            />
            <BagSection
              label="Quick"
              cols={3}
              cells={bagCells(inv, ItemLocation.Quick, QUICK_SLOTS)}
            />
          </div>
          <div class="ov-bag-main">
            <BagSection
              label="Backpack"
              hint={`${String(filled)}/${String(count)}`}
              cols={BAG_COLS}
              cells={bag}
            />
          </div>
        </div>

        {sel === null ? (
          <div class="ov-bag-sel empty">Pick a square to use or drop what is in it.</div>
        ) : (
          <div class="ov-bag-sel" style={{ borderLeftColor: railCss(sel.itemId) }}>
            <div class="ov-row-top">
              <span class="name" style={{ color: rarityCss(sel.itemId) }}>
                {nameOf(sel)}
              </span>
              <span class="kind">{kindLabel(sel)}</span>
              {sel.qty > 1 ? <span class="qty">x{String(sel.qty)}</span> : null}
              {sel.insured ? <span class="tag">ins</span> : null}
              {isConsumable(sel) ? (
                <button
                  type="button"
                  class="ov-act"
                  onClick={() => {
                    host.onUse(sel.uid);
                  }}
                >
                  Use
                </button>
              ) : null}
              <button
                type="button"
                class="ov-act ghost"
                onClick={() => {
                  host.onDrop(sel.uid);
                  set({ bagSel: null });
                }}
              >
                Drop
              </button>
            </div>
            <ItemMeta stack={sel} />
          </div>
        )}
      </div>
    );
  };

  const Card = (): VNode | null => {
    const card = state.card;
    if (!card) return null;
    return (
      <div class="ov-card-wrap">
        <div class="ov-card">
          <h2>Lost in the Band</h2>
          <p class="sub">{card.reason}</p>
          <Pulse />
          <div class="ov-card-actions">
            <button
              type="button"
              class="ov-act"
              style={{ minHeight: '46px' }}
              onClick={() => {
                sound.play('ui_confirm');
                host.onReturnToHub();
              }}
            >
              Return to Lanternfall
            </button>
            <button
              type="button"
              class="ov-act ghost"
              style={{ minHeight: '42px' }}
              onClick={() => {
                openFeedback('What killed the run, and did it feel fair?');
              }}
            >
              Say more
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * One-tap "was that fun?". Deliberately not a form: it is the only feedback most
   * players will ever send, so it costs a single thumb press and posts in the background.
   */
  let pulseSent: 'up' | 'down' | null = null;
  const Pulse = (): VNode => {
    const send = (up: boolean): void => {
      if (pulseSent !== null) return;
      pulseSent = up ? 'up' : 'down';
      draw();
      void submitFeedback(
        {
          category: 'pulse',
          rating: up ? 5 : 1,
          text: '',
          context: host.collector.snapshot(),
        },
        host.token(),
      );
    };
    return (
      <div class="ov-pulse">
        <span class="q">Was that fun?</span>
        <button
          type="button"
          class={pulseSent === 'up' ? 'ov-thumb on' : 'ov-thumb'}
          aria-label="yes"
          onClick={() => {
            send(true);
          }}
        >
          👍
        </button>
        <button
          type="button"
          class={pulseSent === 'down' ? 'ov-thumb on' : 'ov-thumb'}
          aria-label="no"
          onClick={() => {
            send(false);
          }}
        >
          👎
        </button>
      </div>
    );
  };

  // Settings mid-raid. Until 2026-09-05 the bar's Settings button only released the pointer
  // lock, so there was no way to reach Quality without going back to Lanternfall.
  const settingsHost = { onSettingsChanged: () => host.onSettingsChanged() } as unknown as HubHost;
  const SettingsPanel = (): VNode | null => {
    if (!state.settingsOpen) return null;
    return (
      <div class="ov-settings" role="dialog" aria-label="Settings">
        <div class="ov-settings-head">
          <span class="ov-settings-title">Settings</span>
          <span class="ov-settings-hint">L or ESC closes</span>
          <button
            type="button"
            class="ov-settings-close"
            aria-label="close"
            onClick={() => {
              set({ settingsOpen: false });
            }}
          >
            ✕
          </button>
        </div>
        <div class="ov-settings-body">
          <SettingsTab host={settingsHost} />
        </div>
      </div>
    );
  };

  /**
   * The character sheet — C.
   *
   * The same `Loadout` the hub draws, so a player learns one paperdoll, but **read-only** out
   * here on purpose: a raid room holds its own copy of your items (`loadCarriedItems` at join),
   * and `PUT /api/equipment` writes the persistent table underneath it. Swapping a helm mid-raid
   * over HTTP would leave the room and the ledger disagreeing about what you are wearing, and the
   * room would win at extract. Kit changes belong in Lanternfall; the menu says so.
   */
  const CharacterPanel = (): VNode | null => {
    if (!state.charOpen) return null;
    const p = progressStore.current.data;
    return (
      <div class="ov-sheet" role="dialog" aria-label="Character">
        <div class="ov-sheet-head">
          <span class="ov-sheet-title">Character</span>
          <span class="ov-sheet-hint">C or ESC closes</span>
          <button
            type="button"
            class="ov-settings-close"
            aria-label="close"
            onClick={() => {
              set({ charOpen: false, menu: null, tip: null });
            }}
          >
            ✕
          </button>
        </div>
        <div class="ov-sheet-body">
          <Loadout
            items={[...state.inventory]}
            token={host.token()}
            calling={p?.calling ?? null}
            level={p?.level ?? null}
            readOnly
            onChanged={() => {
              /* nothing to re-read: the server pushes S2C.Inventory. */
            }}
            onContext={(stack, x, y) => {
              set({ menu: { uid: stack.uid, x, y }, tip: null });
            }}
            onHover={(stack, x, y) => {
              set({ tip: stack === null ? null : { uid: stack.uid, x, y } });
            }}
          />
        </div>
      </div>
    );
  };

  /** Sabel's counter, in the world. Literally the hub's market panel, inside a raid frame. */
  const VendorPanel = (): VNode | null => {
    if (!state.vendorOpen) return null;
    return (
      <div class="ov-sheet wide" role="dialog" aria-label={SABEL_NAME}>
        <div class="ov-sheet-head">
          <span class="ov-sheet-title">{SABEL_NAME}</span>
          <span class="ov-sheet-hint">ESC closes</span>
          <button
            type="button"
            class="ov-settings-close"
            aria-label="close"
            onClick={() => {
              set({ vendorOpen: false, menu: null, tip: null });
            }}
          >
            ✕
          </button>
        </div>
        <div class="ov-sheet-body">
          <Vendor nonce={state.vendorNonce} token={host.token()} compact />
        </div>
      </div>
    );
  };

  /**
   * The right-click menu. Out in the Band the Drop row is real — that is what `InteractAction.Drop`
   * is for — and Repair is offered only while Sabel's counter is actually open, which is the one
   * place in the world it means anything. Equip is greyed with the reason, because the room owns
   * your kit while you are in it.
   */
  const ContextMenu = (): VNode | null => {
    const open = state.menu;
    const stack = stackByUid(open?.uid);
    if (open === null || stack === null) return null;
    const p = progressStore.current.data;
    const entries: MenuEntry[] = baseItemEntries({
      stack,
      level: p?.level ?? null,
      calling: p?.calling ?? null,
      onEquip: () => {
        /* unreachable: greyed below. */
      },
      onUnequip: () => {
        /* unreachable: greyed below. */
      },
      onDrop: (s) => {
        host.onDrop(s.uid);
        set({ bagSel: null });
      },
    }).map((entry) =>
      entry.key === 'equip' || entry.key === 'unequip'
        ? { ...entry, reason: 'Not out here — change your kit in Lanternfall' }
        : entry,
    );
    if (isConsumable(stack)) {
      entries.unshift({
        key: 'use',
        label: 'Use',
        run: () => {
          host.onUse(stack.uid);
        },
      });
    }
    return (
      <ItemMenu
        stack={stack}
        x={open.x}
        y={open.y}
        entries={entries}
        onClose={() => {
          set({ menu: null });
        }}
      />
    );
  };

  const HoverCard = (): VNode | null => {
    const tip = state.tip;
    const stack = stackByUid(tip?.uid);
    if (tip === null || stack === null || state.menu !== null) return null;
    const p = progressStore.current.data;
    return (
      <ItemTooltip
        stack={stack}
        x={tip.x}
        y={tip.y}
        level={p?.level ?? null}
        calling={p?.calling ?? null}
      />
    );
  };

  const Feedback = (): VNode | null => {
    if (!state.feedback.open) return null;
    return (
      <FeedbackModal
        title="Feedback terminal"
        subtitle="Everything on screen right now goes with it — build, frame times, where you are standing."
        collector={host.collector}
        capture={host.capture}
        token={host.token()}
        screenshotDefault={host.screenshotDefault()}
        seedText={state.feedback.seed}
        onDone={() => {
          set({ feedback: { open: false, seed: '' } });
        }}
        onCancel={() => {
          set({ feedback: { open: false, seed: '' } });
        }}
      />
    );
  };

  function draw(): void {
    render(
      <>
        <Prompt />
        <QuickBar />
        <LootPanel />
        <InventoryPanel />
        <Toasts />
        <Card />
        <Feedback />
        <SettingsPanel />
        <CharacterPanel />
        <VendorPanel />
        <HoverCard />
        <ContextMenu />
      </>,
      root,
    );
  }

  function closeLoot(): void {
    if (state.lootId === 0) return;
    set({ lootId: 0, loot: [] });
    host.onCloseLoot();
  }

  function openFeedback(seed: string): void {
    set({ feedback: { open: true, seed } });
  }

  function pushToast(text: string, tone: ToastTone): void {
    toastSeq += 1;
    const id = toastSeq;
    const next = [...state.toasts, { id, text, tone }];
    set({ toasts: next.slice(Math.max(0, next.length - MAX_TOASTS)) });
    const timer = setTimeout(() => {
      toastTimers.delete(timer);
      set({ toasts: state.toasts.filter((t) => t.id !== id) });
    }, TOAST_MS[tone]);
    toastTimers.add(timer);
  }

  function showToast(itemId: string, qty: number): void {
    const name = ITEMS[itemId]?.name ?? itemId;
    pushToast(qty > 1 ? `+ ${name} x${String(qty)}` : `+ ${name}`, 'loot');
  }

  /**
   * Post-take feedback: while the loot panel is open, a Take (or Take All) lands as a
   * fresh S2C.Inventory. Diff old vs. new totals per item id — merges into an existing
   * stack change its qty rather than its uid, so uid alone cannot tell "new" apart —
   * and toast the first item that grew.
   */
  function toastNewItems(items: readonly ItemStack[]): void {
    if (state.lootId === 0) return;
    const prev = new Map<string, number>();
    for (const s of state.inventory) prev.set(s.itemId, (prev.get(s.itemId) ?? 0) + s.qty);
    const next = new Map<string, number>();
    for (const s of items) next.set(s.itemId, (next.get(s.itemId) ?? 0) + s.qty);
    for (const [itemId, qty] of next) {
      const before = prev.get(itemId) ?? 0;
      if (qty > before) {
        showToast(itemId, qty - before);
        return;
      }
    }
  }

  const startTicker = (): void => {
    if (ticker !== null) return;
    // The clock only needs to be right to the second; 4 Hz keeps the countdown honest
    // without re-rendering the panel tree every frame.
    ticker = setInterval(() => {
      if (state.raidActive) draw();
    }, 250);
  };

  /**
   * The one key the overlay claims for itself: **C**, the character sheet.
   *
   * `input.ts` owns movement and the raid verbs and `raidbar.ts` owns the menu strip's caps; the
   * paperdoll belongs to neither, and wiring it through either would have meant editing a file
   * another pass is holding. So it is bound here, with `raidbar.ts`'s guards copied exactly: no
   * repeats, no modifiers, nothing while a text field has the focus, and nothing at all unless
   * the host says the keypress belongs to the world. ESC closes it through the ladder above.
   */
  function onKeyDown(ev: KeyboardEvent): void {
    if (ev.code !== 'KeyC') return;
    if (ev.repeat || ev.ctrlKey || ev.altKey || ev.metaKey) return;
    const target = ev.target;
    if (target instanceof HTMLElement) {
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }
    }
    if (!host.isActive()) return;
    ev.preventDefault();
    set({ charOpen: !state.charOpen, menu: null, tip: null });
  }
  window.addEventListener('keydown', onKeyDown);

  draw();
  startTicker();

  return {
    clearRaid() {
      set({ raidActive: false, prompt: '' });
    },
    setInventory(items) {
      toastNewItems(items);
      set({ inventory: [...items] });
    },
    showLoot(containerId, items) {
      set({ lootId: containerId, loot: [...items] });
    },
    closeLoot,
    setInventoryOpen(open) {
      set({ invOpen: open, bagSel: null });
    },
    toggleInventory() {
      set({ invOpen: !state.invOpen, bagSel: null });
    },
    takeAll() {
      if (state.lootId !== 0) host.onTakeAll();
    },
    setPrompt(text) {
      if (text === state.prompt) return;
      set({ prompt: text });
    },
    openFeedback,
    closeFeedback() {
      set({ feedback: { open: false, seed: '' } });
    },
    showLost(reason) {
      pulseSent = null;
      set({ card: { reason } });
    },
    hideCard() {
      set({ card: null });
    },
    get panelOpen() {
      return panelOpen();
    },
    get lootContainerId() {
      return state.lootId;
    },
    escape() {
      // The menu is on top of everything and is the cheapest thing to be rid of.
      if (state.menu !== null) {
        set({ menu: null });
        return true;
      }
      if (state.vendorOpen) {
        set({ vendorOpen: false, tip: null });
        return true;
      }
      if (state.charOpen) {
        set({ charOpen: false, tip: null });
        return true;
      }
      if (state.feedback.open) {
        set({ feedback: { open: false, seed: '' } });
        return true;
      }
      if (state.settingsOpen) {
        set({ settingsOpen: false });
        return true;
      }
      if (state.lootId !== 0) {
        closeLoot();
        return true;
      }
      // Escape peels one layer at a time: the picked square first, then the bag itself.
      if (state.bagSel !== null) {
        set({ bagSel: null });
        return true;
      }
      if (state.invOpen) {
        set({ invOpen: false });
        return true;
      }
      // The post-raid card is deliberately not escapable: the only way out is the
      // button that takes the player home, so the run always ends somewhere.
      return false;
    },
    toggleSettings() {
      set({ settingsOpen: !state.settingsOpen, bagSel: null });
    },
    toggleCharacter() {
      set({ charOpen: !state.charOpen, menu: null, tip: null });
    },
    openVendor() {
      if (state.vendorOpen) return;
      // Bumped every time she is spoken to, so the shelf, the purse and the repair list are the
      // ones that exist *now* — a counter that remembers last night's prices is a bug report.
      set({ vendorOpen: true, vendorNonce: state.vendorNonce + 1, menu: null, tip: null });
    },
    pushToast,
    destroy() {
      if (ticker !== null) clearInterval(ticker);
      ticker = null;
      for (const t of toastTimers) clearTimeout(t);
      toastTimers.clear();
      window.removeEventListener('keydown', onKeyDown);
      render(null, root);
    },
  };
}
